/**
 * One changed value, addressed by its path inside the field (`telephone.cell.number`).
 *
 * `change` is set when a whole list element appeared or disappeared, so it reads as an
 * addition or a removal rather than as each of its fields going from or to empty.
 */
export interface ChangedValue {
  path: string;
  from: unknown;
  to: unknown;
  change?: 'added' | 'removed';
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isEmpty = (value: unknown) => value === undefined || value === null || value === '';

const join = (path: string, key: string) => (path ? `${path}.${key}` : key);

/**
 * A list's element keys, when every element carries its own `type` (insurance's primary /
 * secondary / tertiary, a telephone's cell / home) and no two share one; otherwise `null`.
 */
const typeKeys = (list: unknown[]): string[] | null => {
  const keys = list.map((item) =>
    isRecord(item) && (typeof item.type === 'string' || typeof item.type === 'number')
      ? String(item.type)
      : '',
  );
  return keys.every(Boolean) && new Set(keys).size === keys.length ? keys : null;
};

/**
 * Lists are matched element to element, never by position. The card removes a coverage by
 * index, so removing the first of two shifts the second into its place, and a positional diff
 * would read as an edit of both. Elements are paired by `type` when both sides allow it, and
 * otherwise by equal value, so a reorder is no change and what is left unpaired was added or
 * removed.
 */
const diffLists = (from: unknown[], to: unknown[], path: string): ChangedValue[] => {
  const fromKeys = typeKeys(from);
  const toKeys = typeKeys(to);

  if (fromKeys && toKeys) {
    return Array.from(new Set([...fromKeys, ...toKeys])).flatMap((key): ChangedValue[] => {
      const elementPath = join(path, key);
      const left = fromKeys.indexOf(key);
      const right = toKeys.indexOf(key);
      if (left === -1) {
        return [{ path: elementPath, from: undefined, to: to[right], change: 'added' }];
      }
      if (right === -1) {
        return [{ path: elementPath, from: from[left], to: undefined, change: 'removed' }];
      }
      return diffValues(from[left], to[right], elementPath);
    });
  }

  const unpaired = [...to];
  const removed = from.filter((item) => {
    const match = unpaired.findIndex((other) => diffValues(item, other).length === 0);
    if (match === -1) {
      return true;
    }
    unpaired.splice(match, 1);
    return false;
  });

  return [
    ...removed.map(
      (item): ChangedValue => ({ path, from: item, to: undefined, change: 'removed' }),
    ),
    ...unpaired.map((item): ChangedValue => ({ path, from: undefined, to: item, change: 'added' })),
  ];
};

/**
 * The leaves that differ between an old and a new value.
 *
 * The audit log records `demographics` and `insurance` as their whole old and new JSON, so an
 * edit to one phone number is stored as two copies of the entire block. Shown as-is that is a
 * wall of JSON in which the change is hard to find. This walks both and keeps only what
 * changed. `undefined`, `null` and `''` count as the same "empty", so a field the form started
 * sending as `''` does not read as a change.
 */
export const diffValues = (from: unknown, to: unknown, path = ''): ChangedValue[] => {
  const fromList = Array.isArray(from) || isEmpty(from);
  const toList = Array.isArray(to) || isEmpty(to);
  if ((Array.isArray(from) || Array.isArray(to)) && fromList && toList) {
    return diffLists(Array.isArray(from) ? from : [], Array.isArray(to) ? to : [], path);
  }

  if (isRecord(from) || isRecord(to)) {
    const left = isRecord(from) ? from : {};
    const right = isRecord(to) ? to : {};
    const keys = Array.from(new Set([...Object.keys(left), ...Object.keys(right)])).sort();

    return keys.flatMap((key) => diffValues(left[key], right[key], join(path, key)));
  }

  if ((isEmpty(from) && isEmpty(to)) || from === to) {
    return [];
  }
  return [{ path, from, to }];
};

/**
 * A value as a person reads it: empty shown as such, anything structured as compact JSON
 * without its empty fields, so a removed coverage reads as what it held.
 */
export const formatAuditValue = (value: unknown): string => {
  if (isEmpty(value)) {
    return '(empty)';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value, (key, inner) => (key && isEmpty(inner) ? undefined : inner));
  }
  return String(value);
};
